<div class="plan-page-container">
  <div class="plan-page-header">
    <h3 class="m-0">Team Allocation</h3>

    <div class="header-controls">
      <p-select
        [options]="monthOptions"
        [(ngModel)]="selectedMonth"
        (ngModelChange)="onPeriodChange()"
        optionLabel="label"
        optionValue="value"
        appendTo="body"
        styleClass="period-select" />

      <p-select
        [options]="yearOptions"
        [(ngModel)]="selectedYear"
        (ngModelChange)="onPeriodChange()"
        optionLabel="label"
        optionValue="value"
        appendTo="body"
        styleClass="period-select period-select--year" />

      <p-button
        *ngIf="canManageAllocation"
        label="Manage Project - Team Allocation"
        icon="pi pi-external-link"
        severity="info"
        [rounded]="true"
        [disabled]="!projectId"
        (onClick)="manageAllocation()"
        [pTooltip]="projectId
          ? 'Open the Allocation Plan page and add an allocation for this project'
          : 'This pursuit has no project record yet, so it cannot be allocated'"
        tooltipPosition="left" />
    </div>
  </div>

  <!-- Deliberately mirrors the Allocation Plan grid: same column classes, same avatar cell,
       same week bar/track markup, so the two read as one table. -->
  <p-table
    [scrollable]="true" scrollHeight="flex"
    [value]="allocations"
    [loading]="loading"
    [tableStyle]="{ 'width': '100%' }"
    [resizableColumns]="true"
    columnResizeMode="fit"
    styleClass="allocation-table table-flat table-no-xscroll resize-handles-visible">

    <ng-template pTemplate="header">
      <tr>
        <!-- Identity columns only: W1–W5 are uniform narrow numeric cells. -->
        <th pResizableColumn pSortableColumn="employeeName" class="allocation-col-employee">Employee <p-sortIcon field="employeeName" /></th>
        <th pResizableColumn pSortableColumn="designationName" class="allocation-col-project">Designation <p-sortIcon field="designationName" /></th>
        <th pResizableColumn pSortableColumn="invoiceType" class="col-invoice-type">Invoice <p-sortIcon field="invoiceType" /></th>
        <th class="allocation-col-week">W1</th>
        <th class="allocation-col-week">W2</th>
        <th class="allocation-col-week">W3</th>
        <th class="allocation-col-week">W4</th>
        <th class="allocation-col-week">W5</th>
      </tr>
    </ng-template>

    <ng-template pTemplate="body" let-a>
      <tr>
        <td class="allocation-cell-employee">
          <div class="table-employee-wrap table-cell-hover"
               [pTooltip]="a.employeeName + (a.roleTitle ? '\n' + a.roleTitle : '')"
               tooltipPosition="top">
            <span class="table-employee-avatar" [title]="a.employeeName">
              {{ getEmployeeInitials(a.employeeName) }}
            </span>
            <div class="table-employee-info">
              <span class="table-employee-name">{{ a.employeeName }}</span>
              <span class="table-employee-role">{{ a.roleTitle || '—' }}</span>
            </div>
          </div>
        </td>
        <td class="allocation-cell-project table-cell-hover"
            [pTooltip]="'Designation: ' + (a.designationName || '—') + '\nCost Center: ' + (a.costCenterName || '—')"
            tooltipPosition="top">
          <span class="allocation-project-name">{{ a.designationName || '—' }}</span>
          <span class="allocation-project-meta">{{ a.costCenterName || '—' }}</span>
        </td>
        <td class="allocation-cell-invoice">
          <span class="invoice-type-badge invoice-type-badge--{{ getInvoiceTypeSeverity(a.invoiceType) }}">
            <i class="pi pi-circle-fill invoice-type-badge-dot" aria-hidden="true"></i>
            {{ getInvoiceTypeLabel(a.invoiceType) }}
          </span>
        </td>
        <td class="allocation-cell-week">
          <div class="allocation-week-cell">
            <div class="allocation-week-track"><div class="allocation-week-bar" [ngClass]="'allocation-week-bar--' + getWeekPercentSeverity(a.week1Percentage)" [style.width.%]="a.week1Percentage ?? 0"></div></div>
            <span class="allocation-week-value" [ngClass]="'allocation-week-value--' + getWeekPercentSeverity(a.week1Percentage)">{{ a.week1Percentage != null ? (a.week1Percentage + '%') : '—' }}</span>
          </div>
        </td>
        <td class="allocation-cell-week">
          <div class="allocation-week-cell">
            <div class="allocation-week-track"><div class="allocation-week-bar" [ngClass]="'allocation-week-bar--' + getWeekPercentSeverity(a.week2Percentage)" [style.width.%]="a.week2Percentage ?? 0"></div></div>
            <span class="allocation-week-value" [ngClass]="'allocation-week-value--' + getWeekPercentSeverity(a.week2Percentage)">{{ a.week2Percentage != null ? (a.week2Percentage + '%') : '—' }}</span>
          </div>
        </td>
        <td class="allocation-cell-week">
          <div class="allocation-week-cell">
            <div class="allocation-week-track"><div class="allocation-week-bar" [ngClass]="'allocation-week-bar--' + getWeekPercentSeverity(a.week3Percentage)" [style.width.%]="a.week3Percentage ?? 0"></div></div>
            <span class="allocation-week-value" [ngClass]="'allocation-week-value--' + getWeekPercentSeverity(a.week3Percentage)">{{ a.week3Percentage != null ? (a.week3Percentage + '%') : '—' }}</span>
          </div>
        </td>
        <td class="allocation-cell-week">
          <div class="allocation-week-cell">
            <div class="allocation-week-track"><div class="allocation-week-bar" [ngClass]="'allocation-week-bar--' + getWeekPercentSeverity(a.week4Percentage)" [style.width.%]="a.week4Percentage ?? 0"></div></div>
            <span class="allocation-week-value" [ngClass]="'allocation-week-value--' + getWeekPercentSeverity(a.week4Percentage)">{{ a.week4Percentage != null ? (a.week4Percentage + '%') : '—' }}</span>
          </div>
        </td>
        <td class="allocation-cell-week">
          <div class="allocation-week-cell">
            <div class="allocation-week-track"><div class="allocation-week-bar" [ngClass]="'allocation-week-bar--' + getWeekPercentSeverity(a.week5Percentage)" [style.width.%]="a.week5Percentage ?? 0"></div></div>
            <span class="allocation-week-value" [ngClass]="'allocation-week-value--' + getWeekPercentSeverity(a.week5Percentage)">{{ a.week5Percentage != null ? (a.week5Percentage + '%') : '—' }}</span>
          </div>
        </td>
      </tr>
    </ng-template>

    <!-- The condition must sit on the <tr>, not the <ng-template>: PrimeNG collects footer
         templates once via @ContentChildren, so an *ngIf on the template is a no-op and the
         totals row would render under the empty state. -->
    <ng-template pTemplate="footer">
      <tr class="allocation-row-total" *ngIf="allocations.length">
        <td class="allocation-cell-total-label" colspan="3">
          {{ allocations.length }} Allocated — Total
        </td>
        <!-- Same track + bar + value structure as the grid's total row: bar capped at 100%
             width, value still showing the true figure (e.g. 125%). -->
        <td class="allocation-cell-week allocation-cell-total-value" *ngFor="let w of weeks">
          <div class="allocation-week-cell">
            <div class="allocation-week-track">
              <div class="allocation-week-bar allocation-week-bar--total"
                [ngClass]="'allocation-week-bar--' + getWeekPercentSeverity(weekTotal(w))"
                [style.width.%]="weekTotal(w) > 100 ? 100 : weekTotal(w)"></div>
            </div>
            <span class="allocation-week-value allocation-total-value"
              [ngClass]="'allocation-week-value--' + getWeekPercentSeverity(weekTotal(w))">
              {{ weekTotal(w) }}%
            </span>
          </div>
        </td>
      </tr>
    </ng-template>

    <ng-template pTemplate="emptymessage">
      <tr class="table-empty-row">
        <td colspan="8" class="table-empty-cell">
          <div class="empty-state">
            <div class="empty-icon"><i class="pi pi-users"></i></div>
            <p class="empty-message">No team allocated for this month</p>
            <p class="empty-description" *ngIf="projectId && canManageAllocation">
              Use "Manage Project - Team Allocation" to allocate people on the Allocation Plan page.
            </p>
            <p class="empty-description" *ngIf="!projectId">
              This pursuit has no project record yet, so it cannot be allocated.
            </p>
          </div>
        </td>
      </tr>
    </ng-template>
  </p-table>
</div>
